import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { FileText, GitBranch } from 'lucide-react';
import { SubTabsNav } from '@/components/ui/sub-tabs-nav';
import { WorkspaceButton } from '@/components/ui/workspace/button';
import { HR_TABS, tabLabel, type HrTab } from './model';

/**
 * HealthReadinessHeader (T-0242 / E22-F01) — the header block over every
 * Health & Readiness tab, the way `ContractHeader` heads Contract
 * Compliance (prototype `page()`):
 *
 *   Health & Readiness · <tab>              [Capture snapshot] [Report]
 *   <meta: domains · go-live criteria · planned go-live · last snapshot>
 *   Overview · Domains · Go-live criteria · Findings · Interview notes · Report · Snapshots & trend
 *
 * Capture snapshot shows only with a handler (the API keeps it to a lead);
 * Report opens the Report tab and is not offered on it.
 */
export interface HealthReadinessHeaderProps {
  engagementId: string;
  active: HrTab;
  /** The meta line, from the composite read; absent while it loads. */
  meta?: ReactNode;
  onCaptureSnapshot?: () => void;
  capturing?: boolean;
  /** What a capture just froze, or why it failed. */
  status?: ReactNode;
}

const TAB_PATH = {
  overview: '',
  domains: '/domains',
  criteria: '/criteria',
  findings: '/findings',
  interviews: '/interviews',
  report: '/report',
  snapshots: '/snapshots',
} as const satisfies Record<HrTab, string>;

export function HealthReadinessHeader({
  engagementId,
  active,
  meta,
  onCaptureSnapshot,
  capturing,
  status,
}: HealthReadinessHeaderProps) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-h1 font-semibold text-ink-900">
            Health &amp; Readiness ·{' '}
            <span className="font-medium text-ink-500">{tabLabel(active)}</span>
          </h1>
          {meta ? <div className="text-meta text-ink-500">{meta}</div> : null}
          {status ? (
            <div className="text-meta text-ink-700" role="status">
              {status}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {onCaptureSnapshot ? (
            <WorkspaceButton
              variant="secondary"
              onClick={onCaptureSnapshot}
              disabled={capturing}
              aria-label="Capture snapshot"
            >
              <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
              {capturing ? 'Capturing…' : 'Capture snapshot'}
            </WorkspaceButton>
          ) : null}
          {active === 'report' ? null : (
            <WorkspaceButton variant="primary" asChild>
              <Link
                to="/engagements/$engagementId/health-readiness/report"
                params={{ engagementId }}
              >
                <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                Report
              </Link>
            </WorkspaceButton>
          )}
        </div>
      </div>
      <SubTabsNav
        label="Health & Readiness"
        active={active}
        tabs={HR_TABS.map((t) => ({
          key: t.slug,
          label: t.label,
          to: `/engagements/$engagementId/health-readiness${TAB_PATH[t.slug]}` as const,
          params: { engagementId },
        }))}
      />
    </div>
  );
}
